import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from '@/integrations/supabase/client';
import { AdminLoginHistoryEntry } from '@/hooks/useAdminLoginSessions';
import { Loader2 } from 'lucide-react';

interface AdminLoginLocationMapProps {
  sessions: AdminLoginHistoryEntry[];
}

const AdminLoginLocationMap: React.FC<AdminLoginLocationMapProps> = ({ sessions }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const mapLoadedRef = useRef(false); // Fix closure bug in timeout
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize map once
  useEffect(() => {
    let mounted = true;
    let loadTimeout: NodeJS.Timeout;

    const initializeMap = async () => {
      if (!mapContainer.current || map.current) return;

      try {
        console.log('🗺️ Initializing admin map...');
        console.log('Map container dimensions:', mapContainer.current?.clientWidth, 'x', mapContainer.current?.clientHeight);
        setIsLoading(true);
        
        // Fetch Mapbox token
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        
        if (error || !data?.token) {
          console.error('❌ Failed to fetch Mapbox token:', error);
          throw new Error('Failed to retrieve Mapbox token');
        }

        console.log('✅ Admin map token retrieved successfully');

        if (!mounted) return;

        mapboxgl.accessToken = data.token;
        console.log('🌍 Creating admin Mapbox instance...');
        
        // Initialize map with globe projection
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/dark-v11',
          projection: 'globe',
          zoom: 1.5,
          center: [30, 15],
          pitch: 45,
        });

        // Timeout fallback - use ref to avoid closure bug
        loadTimeout = setTimeout(() => {
          if (!mapLoadedRef.current && mounted) {
            console.error('⏱️ Admin map load timeout - map did not fire load event within 15 seconds');
            setError('Map took too long to load. Please refresh the page.');
            setIsLoading(false);
          }
        }, 15000);

        // Add navigation controls
        map.current.addControl(
          new mapboxgl.NavigationControl({
            visualizePitch: true,
          }),
          'top-right'
        );

        // Disable scroll zoom for better UX
        map.current.scrollZoom.disable();

        // Add atmosphere and fog effects
        map.current.on('style.load', () => {
          if (!map.current) return;
          console.log('Admin map style loaded');
          
          map.current.setFog({
            color: 'rgb(30, 30, 50)',
            'high-color': 'rgb(10, 10, 30)',
            'horizon-blend': 0.2,
          });
        });

        // Rotation animation settings
        const secondsPerRevolution = 240;
        const maxSpinZoom = 5;
        const slowSpinZoom = 3;
        let userInteracting = false;
        let spinEnabled = true;

        function spinGlobe() {
          if (!map.current) return;
          
          const zoom = map.current.getZoom();
          if (spinEnabled && !userInteracting && zoom < maxSpinZoom) {
            let distancePerSecond = 360 / secondsPerRevolution;
            if (zoom > slowSpinZoom) {
              const zoomDif = (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
              distancePerSecond *= zoomDif;
            }
            const center = map.current.getCenter();
            center.lng -= distancePerSecond;
            map.current.easeTo({ center, duration: 1000, easing: (n) => n });
          }
        }

        // Event listeners for interaction
        map.current.on('mousedown', () => {
          userInteracting = true;
        });
        
        map.current.on('dragstart', () => {
          userInteracting = true;
        });
        
        map.current.on('mouseup', () => {
          userInteracting = false;
          spinGlobe();
        });
        
        map.current.on('touchend', () => {
          userInteracting = false;
          spinGlobe();
        });

        map.current.on('moveend', () => {
          spinGlobe();
        });

        // Handle Mapbox errors
        map.current.on('error', (e) => {
          console.error('❌ Admin Mapbox error:', e.error);
          if (mounted) {
            setError(`Map error: ${e.error?.message || 'Unknown Mapbox error'}`);
            setIsLoading(false);
            clearTimeout(loadTimeout);
          }
        });

        // Wait for map to load
        map.current.on('load', () => {
          if (!mounted) return;
          console.log('✅ Admin map fully loaded and ready');
          mapLoadedRef.current = true; // Update ref to fix closure bug
          clearTimeout(loadTimeout);
          setMapLoaded(true);
          setIsLoading(false);
          spinGlobe();
        });

      } catch (err) {
        console.error('Error initializing admin map:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load map');
          setIsLoading(false);
        }
      }
    };

    initializeMap();

    return () => {
      mounted = false;
      clearTimeout(loadTimeout);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Add/update markers when sessions change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    console.log(`Updating admin markers for ${sessions.length} sessions`);

    // Remove existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Filter sessions with valid coordinates
    const validSessions = sessions.filter(
      session => session.latitude !== null && session.longitude !== null
    );

    console.log(`Found ${validSessions.length} sessions with coordinates`);

    // Add markers for each login session
    validSessions.forEach((session) => {
      if (!map.current || !session.latitude || !session.longitude) return;

      const markerColor = session.is_suspicious ? '#ef4444' : '#22c55e';
      
      const el = document.createElement('div');
      el.className = 'admin-login-marker';
      el.style.backgroundColor = markerColor;
      el.style.width = '12px';
      el.style.height = '12px';
      el.style.borderRadius = '50%';
      el.style.border = '2px solid white';
      el.style.cursor = 'pointer';
      el.style.boxShadow = `0 0 10px ${markerColor}`;

      const popup = new mapboxgl.Popup({ 
        offset: 25,
        className: 'admin-login-popup'
      }).setHTML(`
        <div style="padding: 8px; min-width: 200px;">
          <div style="font-weight: bold; margin-bottom: 8px; color: ${markerColor};">
            ${session.is_suspicious ? '⚠️ Suspicious Login' : '✓ Normal Login'}
          </div>
          <div style="margin-bottom: 4px;">
            <strong>User:</strong> ${session.username || 'Unknown'}
          </div>
          <div style="margin-bottom: 4px;">
            <strong>Email:</strong> ${session.user_email || 'N/A'}
          </div>
          <div style="margin-bottom: 4px;">
            <strong>Location:</strong> ${session.city || 'Unknown'}, ${session.country || 'Unknown'}
          </div>
          <div style="margin-bottom: 4px;">
            <strong>Time:</strong> ${new Date(session.login_time).toLocaleString()}
          </div>
          <div style="margin-bottom: 4px;">
            <strong>IP:</strong> ${session.ip_address}
          </div>
          ${session.user_agent ? `
            <div style="margin-top: 8px; font-size: 11px; color: #666;">
              ${session.user_agent.substring(0, 60)}...
            </div>
          ` : ''}
        </div>
      `);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([session.longitude, session.latitude])
        .setPopup(popup)
        .addTo(map.current);

      markers.current.push(marker);
    });
  }, [sessions, mapLoaded]);

  if (error) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center bg-muted/20 rounded-lg border border-destructive/20">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Failed to load map</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center bg-muted/20 rounded-lg">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading global login map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[500px]">
      <div ref={mapContainer} className="absolute inset-0 rounded-lg shadow-lg" />
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent to-background/10 rounded-lg" />
      
      {/* Custom CSS for markers and popups */}
      <style>{`
        .admin-login-marker {
          transition: all 0.2s ease;
        }
        .admin-login-marker:hover {
          transform: scale(1.3);
          z-index: 10;
        }
        .admin-login-popup .mapboxgl-popup-content {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(10px);
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
        .admin-login-popup .mapboxgl-popup-tip {
          border-top-color: rgba(255, 255, 255, 0.98);
        }
      `}</style>
    </div>
  );
};

export default AdminLoginLocationMap;
